import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { groupBy } from 'lodash';
import cx from 'classnames';

import Checkbox from 'components/Checkbox';
import { DisabledTooltipLayer } from './SelectFromList';
import EventTracking from 'lib/EventTracking';
import GroupAvatar from 'groups/components/GroupAvatar';
import MediaObject from 'components/MediaObject';
import TargetFilterMenu from 'chat/components/TargetFilterMenu';
import Text from 'components/Text';
import { selectRecipient } from 'reducers/roleBasedMessagingSearch';
import { ComposerContext } from './ComposerContext';
import type { UIEventContext } from './ComposerContext';
import TooltipTrigger from 'components/TooltipTrigger';
import Icon from 'components/Icon';
import CurrentUser from 'auth/CurrentUser';
import { ComposerGroup, GroupRecipient, MessageFilterType } from 'types/typescript/messagingTypes';

type Props = {
  recipient: GroupRecipient;
  recipientIsSelected: boolean;
  disabledCopy: string | undefined | null;
  onSelectRecipient: (
    recipient: GroupRecipient,
    filter?: MessageFilterType | null,
    preventDispatchInternalSelectRecipient?: boolean
  ) => any;
};

class GroupRecipientItem extends React.Component<Props> {
  logSelectionEvent = (uiEventContext: UIEventContext) => {
    if (this.props.recipientIsSelected) {
      EventTracking.trackKey('announcements.share_to.group.unselect.click', uiEventContext);
      return;
    }
    EventTracking.trackKey('announcements.share_to.group.select.click', uiEventContext);
  };

  handleTopLevelSelection = () => {
    this.props.onSelectRecipient(this.props.recipient, null);
  };

  componentDidMount() {
    if (this.props.recipientIsSelected) {
      // Do not trigger the selection otherwise we would uncheck it, this just makes sure the selection is initialized
      this.props.onSelectRecipient(this.props.recipient, null, true);
    }
  }

  renderGroupRecipient = () => {
    const { recipient, recipientIsSelected } = this.props;
    const { group } = recipient;
    const groupedFilters = groupBy(recipient.filters, 'type');
    const hasFilters = Object.keys(groupedFilters).length > 0;

    return (
      <MediaObject gutter="small" center>
        <GroupAvatar group={group} size="small" />
        <div>
          <Text size="small" element="div" truncate>
            {group.className}
          </Text>
          <MediaObject gutter={hasFilters ? 'medium' : 'none'} center>
            {hasFilters && (
              <TargetFilterMenu
                filters={groupedFilters}
                recipient={recipient}
                recipientIsSelected={recipientIsSelected}
                dataTestSelector={`split-composer-group-filter-${group.id}`}
              />
            )}
            {this.canNotReceiveSMS(group) && (
              <TooltipTrigger
                placement="bottom"
                tooltip="Message will only be delivered by app or email"
              >
                <Icon color="graphite" name="no-sms" size="large" data-testid="no-sms-icon" />
              </TooltipTrigger>
            )}
          </MediaObject>
        </div>
      </MediaObject>
    );
  };

  canNotReceiveSMS(group: ComposerGroup) {
    const isAClass = group.type !== 'organization';
    const showSmsLimits = CurrentUser.getUser()?.hasFeature('sms-limits.web.sept-23');
    return (
      isAClass &&
      ((group.canSendSms === false && showSmsLimits) || group.needsK12SenderVerification)
    );
  }

  render() {
    const { recipient, disabledCopy, recipientIsSelected } = this.props;
    const { group, id } = recipient;

    const component = (
      <ComposerContext.Consumer>
        {(composerContext) => (
          <li
            className={cx('RecipientItem', {
              'RecipientItem--disabled': !!disabledCopy,
            })}
            key={id}
            onClick={() => {
              this.handleTopLevelSelection();
              this.logSelectionEvent(composerContext.getUIEventContext());
            }}
            data-test-selector={`split-composer-group-${group.id}`}
          >
            <MediaObject figure="right" center>
              {this.renderGroupRecipient()}
              <Checkbox onChange={this.handleTopLevelSelection} checked={recipientIsSelected} />
            </MediaObject>
          </li>
        )}
      </ComposerContext.Consumer>
    );

    if (disabledCopy) {
      return <DisabledTooltipLayer text={disabledCopy}>{component}</DisabledTooltipLayer>;
    }

    return component;
  }
}

const mapDispatchToProps = (dispatch: Function) => {
  return bindActionCreators(
    {
      onSelectRecipient: selectRecipient,
    },
    // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
    dispatch
  );
};

export { GroupRecipientItem };

export default connect(undefined, mapDispatchToProps)(GroupRecipientItem);
